import { Stack, Text, Tooltip } from '@mantine/core'
import { IconCheck, IconAlertTriangle, IconX, IconMinus, IconClock } from '@tabler/icons-react'
import { Delta } from '@lighthouse/design'
import { NOT_MEASURED, duration, humanizePhase } from '../../utils/format'
import type { RunStage, StageStatus } from '../../types/observatory'

/**
 * The ETL run lifecycle strip.
 *
 * Built in W1.6 to prove the shape before four surfaces depend on it (ETL
 * Explorer, Pipeline View, Timeline View, Entity History). It is the one piece of
 * the mockup that no chart library provides, so it was the biggest open UI
 * question in W2 — resolving it now means W2 is telemetry work, not design work.
 */

function stageIcon(status: StageStatus) {
  switch (status) {
    case 'done': return <IconCheck size={13} stroke={3} />
    case 'warning': return <IconAlertTriangle size={12} stroke={2.5} />
    case 'failed': return <IconX size={13} stroke={3} />
    case 'active': return <IconClock size={12} stroke={2.5} />
    case 'skipped': return <IconMinus size={12} stroke={2.5} />
    case 'pending': return null
  }
}

/**
 * `totalSeconds` is null when nothing measured the run's length — a row that
 * never started, or a stale one whose end is unknown — and the total then reads
 * as the dash, never 00:00:00.
 *
 * `caption` is what the page can say beyond the recorded stages. Stages are
 * written when the entity FINISHES, so a running entity shows only Queued until
 * then (ChunkedEltRunner.cs:98-99, :164); the caption carries its live chunk
 * phase instead.
 */
export function StageStrip({ stages, totalSeconds, caption }: {
  stages: RunStage[]
  totalSeconds: number | null
  caption?: string | null
}) {
  return (
    <Stack gap={6}>
    <div className="lh-stage-strip-wrap">
    <div className="lh-stage-strip" role="list" aria-label="Run stages">
      {stages.map((stage, i) => {
        const prev = i > 0 ? stages[i - 1].status : undefined
        return (
          <div
            className="lh-stage"
            key={stage.name}
            role="listitem"
            data-status={stage.status}
            data-prev={prev}
          >
            {/* Status is carried by the icon and the label, never by colour
                alone — the same rule the status badges follow. */}
            <span className="lh-stage-node" aria-hidden="true">{stageIcon(stage.status)}</span>
            <span className="lh-stage-label">{humanizePhase(stage.name)}</span>
            <span className="lh-stage-duration">
              {stage.durationSeconds !== null ? duration(stage.durationSeconds) : '—'}
            </span>
            {/* Duration vs baseline reuses the intent-keyed Delta: for a
                duration LOWER is better, so a stage running 43% slow reads
                adverse without the caller having to reason about sign. */}
            {stage.vsBaselinePercent !== null && (
              <Tooltip label="Compared with this stage's 30-day baseline" withArrow>
                <span>
                  <Delta change={stage.vsBaselinePercent} polarity="lower-is-better" />
                </span>
              </Tooltip>
            )}
            {stage.note && <span className="lh-stage-note">{stage.note}</span>}
            <span className="lh-visually-hidden">{stage.status}</span>
          </div>
        )
      })}
    </div>

      {/* Outside the scroller on purpose — see the CSS note. */}
      <div className="lh-stage-total">
        <span className="lh-stage-total-label">Total</span>
        <span className="lh-stage-total-value">{totalSeconds == null ? NOT_MEASURED : duration(totalSeconds)}</span>
      </div>
    </div>

      {/* Below the strip, not inside it: the wrap is one flex row, and a caption
          there would squeeze the stages. */}
      {caption && <Text size="xs" c="dimmed">{caption}</Text>}
    </Stack>
  )
}
